import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { api } from '../../api/client';
import { useAuthStore } from '../../store/authStore';
import { ROUTES } from '../../api/routes';
import { useProfessorTurmas } from '../../api/hooks/useProfessorTurmas';
import { useHojeReferencia } from '../../api/hooks/useSimClock';
import type { ConsolidacaoResponse, FrequenciaTurmaResponse } from '../../api/hooks/useRelatorios';

export type ProfessorTurmaFreq = {
  turmaId: string;
  nome: string;
  /** Turno operacional — evita “2º Ano D” duplicado na lista. */
  turno?: string;
  label: string;
  media: number;
  emRisco: number;
  alunos: number;
};

function labelTurma(nome: string, turno?: string) {
  const n = (nome || '').trim() || 'Turma';
  const t = (turno || '').trim();
  if (!t) return n;
  // Evita "Manhã · Manhã" se o nome já trouxer o turno
  if (n.toLowerCase().includes(t.toLowerCase())) return n;
  return `${n} · ${t}`;
}

/**
 * Indicadores do dashboard do professor — somente turmas da grade dele
 * (não usa consolidação da escola inteira).
 */
export function useProfessorDashboardAnalytics(professorId?: string) {
  const escolaId = useAuthStore((s) => s.escolaId);
  const { hojeStr } = useHojeReferencia();
  const anoLetivo = useMemo(() => {
    if (hojeStr && /^\d{4}-\d{2}-\d{2}$/.test(hojeStr)) {
      return Number(hojeStr.slice(0, 4));
    }
    return new Date().getFullYear();
  }, [hojeStr]);
  const { turmas, isLoading: loadingTurmas } = useProfessorTurmas(professorId);

  const freqQueries = useQueries({
    queries: turmas.map((t) => ({
      queryKey: ['relatorios', 'frequencia', escolaId, t.id, anoLetivo, 'prof-dash'],
      queryFn: async (): Promise<
        ProfessorTurmaFreq & { presentes: number; ausentes: number }
      > => {
        const { data } = await api.get<FrequenciaTurmaResponse>(
          ROUTES.relatorios.frequencia(escolaId!, t.id),
          { params: { anoLetivo } },
        );
        let presentes = 0;
        let ausentes = 0;
        for (const a of data.alunos || []) {
          presentes += Number(a.presentes) || 0;
          ausentes += Number(a.ausentes) || 0;
        }
        return {
          turmaId: t.id,
          nome: t.nome,
          turno: t.turno || '',
          label: labelTurma(t.nome, t.turno),
          media: data.mediaFrequencia ?? 0,
          emRisco: data.alunosEmRisco ?? 0,
          alunos: data.totalAlunos ?? 0,
          presentes,
          ausentes,
        };
      },
      enabled: !!escolaId && !!professorId && !!t.id,
      staleTime: 180_000,
    })),
  });

  const turmasFrequencia = useMemo(() => {
    // Dedup estrito por turmaId (evita fantasma se a grade reaparecer em dias diferentes).
    const byId = new Map<string, ProfessorTurmaFreq>();
    for (const q of freqQueries) {
      const d = q.data;
      if (!d?.turmaId) continue;
      byId.set(d.turmaId, {
        turmaId: d.turmaId,
        nome: d.nome,
        turno: d.turno,
        label: d.label || labelTurma(d.nome, d.turno),
        media: d.media,
        emRisco: d.emRisco,
        alunos: d.alunos,
      });
    }
    return Array.from(byId.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [freqQueries]);

  const resumo = useMemo((): ConsolidacaoResponse['resumo'] | undefined => {
    if (!turmas.length && !loadingTurmas) {
      return {
        totalAlunos: 0,
        totalTurmas: 0,
        mediaFrequencia: 0,
        totalPresencasDias: 0,
        totalAusencias: 0,
        totalIncidentes: 0,
        totalAvaliacoes: 0,
        alunosEmRisco: 0,
      };
    }
    if (freqQueries.some((q) => q.isLoading) || !freqQueries.length) return undefined;

    const rows = freqQueries.map((q) => q.data).filter(Boolean) as Array<{
      alunos: number;
      media: number;
      emRisco: number;
      presentes: number;
      ausentes: number;
    }>;

    const totalAlunos = rows.reduce((s, r) => s + r.alunos, 0);
    const presentes = rows.reduce((s, r) => s + r.presentes, 0);
    const ausentes = rows.reduce((s, r) => s + r.ausentes, 0);
    const emRisco = rows.reduce((s, r) => s + r.emRisco, 0);
    const mediaPonderada =
      totalAlunos > 0
        ? rows.reduce((s, r) => s + r.media * r.alunos, 0) / totalAlunos
        : 0;
    const registros = presentes + ausentes;
    const mediaPorRegistros =
      registros > 0 ? Math.round((presentes / registros) * 1000) / 10 : mediaPonderada;

    return {
      totalAlunos,
      totalTurmas: turmas.length,
      mediaFrequencia: Math.round(mediaPorRegistros * 10) / 10,
      totalPresencasDias: registros,
      totalAusencias: ausentes,
      totalIncidentes: 0,
      totalAvaliacoes: 0,
      alunosEmRisco: emRisco,
    };
  }, [freqQueries, turmas.length, loadingTurmas]);

  const isLoadingFreq = freqQueries.some((q) => q.isLoading);

  return {
    isLoading: loadingTurmas || isLoadingFreq,
    turmasFrequencia,
    resumo,
  };
}
